import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CarEquipmentTypeManager.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const CarEquipmentTypeManager = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/car-equipment-types/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setTypes(response.data);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
      setError('Failed to load equipment types');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingType) {
        await axios.put(
          `${API_BASE_URL}/api/car-equipment-types/${editingType.id}/`,
          formData,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/car-equipment-types/`,
          formData,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
        );
      }

      fetchTypes();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving equipment type:', error);
      setError(error.response?.data?.code?.[0] || 'Failed to save equipment type');
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({ name: type.name, code: type.code });
    setShowModal(true);
  };

  const handleDelete = async (type) => {
    try {
      // Check if type is used
      const usageResponse = await axios.get(
        `${API_BASE_URL}/api/car-equipment-types/${type.id}/check-usage/`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
      );

      if (usageResponse.data.is_used) {
        if (!window.confirm(`This type is used by ${usageResponse.data.cars_count} vehicle(s). Are you sure you want to deactivate it?`)) {
          return;
        }
      } else {
        if (!window.confirm(`Are you sure you want to delete "${type.name}"?`)) {
          return;
        }
      }

      await axios.delete(
        `${API_BASE_URL}/api/car-equipment-types/${type.id}/`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
      );

      fetchTypes();
    } catch (error) {
      console.error('Error deleting equipment type:', error);
      setError('Failed to delete equipment type');
    }
  };

  const handleOpenModal = () => {
    setEditingType(null);
    setFormData({ name: '', code: '' });
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
    setFormData({ name: '', code: '' });
    setError('');
  };

  if (loading) {
    return (
      <div className="equipment-type-manager">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="equipment-type-manager">
      <div className="manager-header">
        <h2>Equipment Types</h2>
        <button className="btn-add" onClick={handleOpenModal}>
          + Add New Type
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="types-grid">
        {types.map((type) => (
          <div key={type.id} className="type-card">
            <div className="type-info">
              <h3>{type.name}</h3>
              <p className="type-code">{type.code}</p>
              {type.created_by_name && (
                <p className="type-meta">Created by {type.created_by_name}</p>
              )}
            </div>
            <div className="type-actions">
              <button className="btn-edit" onClick={() => handleEdit(type)}>
                Edit
              </button>
              <button className="btn-delete" onClick={() => handleDelete(type)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {types.length === 0 && (
        <div className="empty-state">
          <p>No equipment types found. Add your first type to get started.</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingType ? 'Edit Equipment Type' : 'Add New Equipment Type'}</h3>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div className="form-error">{error}</div>}

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Lighting Tower"
                  required
                />
              </div>

              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g., lighting_tower"
                  pattern="[a-z_]+"
                  title="Only lowercase letters and underscores allowed"
                  required
                  disabled={editingType} // Can't change code when editing
                />
                <small>Lowercase letters and underscores only</small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingType ? 'Save Changes' : 'Create Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarEquipmentTypeManager;
